import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useBookmark = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);

  // Fetch the caller's saved bookmarks
  const fetchBookmarks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.aipdGetBookmarks());
      setBookmarks(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching bookmarks:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Save (create) a bookmark for a report page
  const saveBookmark = useCallback(async (bookmarks) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdSaveBookmarks(), bookmarks);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error saving bookmark:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Remove a bookmarked report
  const deleteBookmark = useCallback(async (reportId, pagename, title) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.delete(apiUrl.aipdDeleteBookmarks(), {
        data: { reportId, pagename, title },
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting bookmark:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Rename a bookmarked report
  const updateBookmarkTitle = useCallback(async (id, title) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdUpdateBookmarkTitles(), {
        id,
        title,
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating bookmark title:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    loading,
    error,
    bookmarks,
    fetchBookmarks,
    saveBookmark,
    deleteBookmark,
    updateBookmarkTitle,
    clearError,
  };
};

export default useBookmark;
